/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from "react";

const DEBOUNCE_MS = 100;

/**
 * Shared hook for responsive window width tracking with debounced resize handling.
 * Replaces duplicate resize listener patterns across 10+ components.
 *
 * @param breakpoint - Optional breakpoint to further optimise re-renders. When
 *   provided, state only updates when the window width crosses the threshold —
 *   not on every pixel change. Without a breakpoint, state updates are debounced
 *   at 100 ms to prevent 100+ re-renders per resize drag.
 * @returns Current window width
 */
export function useWindowWidth(breakpoint?: number): number {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleResize = (): void => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        const currentWidth = window.innerWidth;
        if (breakpoint !== undefined) {
          setWidth((prev) => {
            const crossedBreakpoint =
              (prev < breakpoint && currentWidth >= breakpoint) ||
              (prev >= breakpoint && currentWidth < breakpoint);
            return crossedBreakpoint ? currentWidth : prev;
          });
        } else {
          setWidth(currentWidth);
        }
      }, DEBOUNCE_MS);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (debounceTimer !== null) clearTimeout(debounceTimer);
    };
  }, [breakpoint]);

  return width;
}
